import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import agentPreferenceService from '../services/agentPreferenceService';
import { useAuth } from './AuthContext';

const AgentPreferencesContext = createContext(null);

export function AgentPreferencesProvider({ children }) {
  const [hiddenAgentIds, setHiddenAgentIds] = useState(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const { user, loading: authLoading } = useAuth();

  useEffect(() => {
    if (authLoading) return;
    if (!user?.id) {
      setHiddenAgentIds(new Set());
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    async function load() {
      try {
        const ids = await agentPreferenceService.getHiddenAgentIds();
        if (!cancelled) setHiddenAgentIds(new Set(ids));
      } catch (error) {
        console.error('[AgentPreferences] Failed to load:', error);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [user?.id, authLoading]);

  const toggleHidden = useCallback(async (rolId) => {
    const isCurrentlyHidden = hiddenAgentIds.has(rolId);
    const newHidden = !isCurrentlyHidden;

    // Optimistic update
    setHiddenAgentIds(prev => {
      const next = new Set(prev);
      if (newHidden) next.add(rolId);
      else next.delete(rolId);
      return next;
    });

    try {
      await agentPreferenceService.setAgentHidden(rolId, newHidden);
    } catch (error) {
      console.error('[AgentPreferences] Failed to toggle:', error);
      // Revert on error
      setHiddenAgentIds(prev => {
        const next = new Set(prev);
        if (newHidden) next.delete(rolId);
        else next.add(rolId);
        return next;
      });
    }
  }, [hiddenAgentIds]);

  return (
    <AgentPreferencesContext.Provider value={{ hiddenAgentIds, toggleHidden, isLoading }}>
      {children}
    </AgentPreferencesContext.Provider>
  );
}

export function useAgentPreferences() {
  const ctx = useContext(AgentPreferencesContext);
  if (!ctx) throw new Error('useAgentPreferences must be used within AgentPreferencesProvider');
  return ctx;
}
